/**
 * Resmo Talent Directory — gated to verified employers.
 *
 * Search UI: free-text query + location filter + minimum years-of-experience
 * slider + skill chip filters. Results render as cards linking to the full
 * public résumé page at /r/<public_id>.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, MapPin, Users, LogOut, Sparkles, ArrowRight, Store, Star } from "lucide-react";
import { useEmployer } from "../components/employer/EmployerProtected";
import SkillsExtractorWidget from "../components/employer/SkillsExtractorWidget";
import ImpersonationBanner from "../components/employer/ImpersonationBanner";
import {
  NAVY, ORANGE, MUSTARD,
  MIDNIGHT_BG_STYLE, GLASS_HEADER_STYLE,
} from "../components/employer/employerTheme";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Discover() {
  const navigate = useNavigate();
  const { employer } = useEmployer();
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [minYears, setMinYears] = useState(0);
  const [skills, setSkills] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(null);

  const runSearch = useCallback(async () => {
    setBusy(true);
    try {
      const skillList = skills.split(",").map((s) => s.trim()).filter(Boolean);
      const r = await axios.post(`${BACKEND_URL}/api/discover/search`, {
        q, location, min_years: Number(minYears) || 0,
        skills: skillList, limit: 30,
      }, { withCredentials: true });
      setResults(r.data.results || []);
      setCount(r.data.count);
    } catch (err) {
      console.error("discover search failed", err);
    } finally { setBusy(false); }
  }, [q, location, minYears, skills]);

  // Run an initial "everyone" query on first load so recruiters see the
  // directory has candidates before they start filtering.
  useEffect(() => { runSearch(); }, [runSearch]);

  const logout = async () => {
    const isAdminShadow = employer?.is_admin_shadow;
    try {
      if (isAdminShadow) {
        await axios.post(`${BACKEND_URL}/api/logout`, {}, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/employer/logout`, {}, { withCredentials: true });
      }
    } catch { /* ignore logout errors */ }
    navigate(isAdminShadow ? "/login" : "/employer/login", { replace: true });
  };

  return (
    <div className="min-h-screen" style={MIDNIGHT_BG_STYLE}>
      <ImpersonationBanner
        employer={employer}
        onExit={() => { window.location.href = "/admin/employers"; }}
      />
      {/* Header — Resmo wordmark + glassy band, matches Store/Shortlist */}
      <div className="border-b backdrop-blur-md" style={GLASS_HEADER_STYLE}
           data-testid="discover-header">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/discover" style={{ textDecoration: "none" }} className="flex items-center shrink-0" data-testid="discover-brand">
              <span
                className="font-display uppercase leading-none"
                style={{ fontSize: "1.6rem", color: "white", letterSpacing: "-0.02em", fontWeight: 900 }}
              >
                <span style={{ color: ORANGE }}>R</span>esmo
              </span>
              <span
                className="ml-2 hidden md:inline-block font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
                style={{ fontSize: "9px", color: NAVY, background: MUSTARD, fontWeight: 800, border: `1px solid ${MUSTARD}` }}
              >
                · Talent Directory
              </span>
            </Link>
            <span className="hidden md:inline-block" style={{ color: "rgba(255,255,255,0.28)" }}>•</span>
            <div className="min-w-0">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest"
                   style={{ color: MUSTARD, fontWeight: 700 }}>
                · Quick search
              </div>
              <div className="font-display uppercase leading-tight truncate"
                   style={{ fontSize: "1.15rem", color: "white", letterSpacing: "-0.01em" }}>
                Find candidates that fit.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/employer/store"
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
              style={{ background: "#FFC145", color: "#0F1B2D", border: "1px solid #FFC145", fontWeight: 800, textDecoration: "none" }}
              data-testid="discover-nav-store"
            >
              <Store size={11} /> Candidates Store
            </Link>
            <Link
              to="/employer/shortlist"
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
              style={{ color: "#FFC145", border: "1px solid #FFC145", fontWeight: 700, textDecoration: "none" }}
              data-testid="discover-nav-shortlist"
            >
              <Star size={11} /> Shortlist
            </Link>
            <span className="hidden md:inline-block text-[11px]" style={{ color: "#94A3B8" }}>
              {employer?.company} · {employer?.email}
            </span>
            <button onClick={logout}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                    style={{ background: "transparent", color: "#FFC145", border: "1px solid #FFC145", fontWeight: 700 }}
                    data-testid="discover-logout">
              <LogOut size={11} /> Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-5">
        <div className="mb-4">
          <SkillsExtractorWidget
            onApplySkills={(arr) => {
              // Merge into the comma-separated skills string (dedupe case-insensitive)
              const current = skills.split(",").map((s) => s.trim()).filter(Boolean);
              const seen = new Set(current.map((s) => s.toLowerCase()));
              (arr || []).forEach((raw) => {
                const s = (raw || "").trim();
                if (s && !seen.has(s.toLowerCase())) {
                  current.push(s);
                  seen.add(s.toLowerCase());
                }
              });
              setSkills(current.join(", "));
            }}
            defaultOpen={false}
            testIdPrefix="discover-skills-extractor"
          />
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); runSearch(); }}
          className="rounded-md p-4 md:p-5"
          style={{ background: "white", border: "1.5px solid #CBD5E1", boxShadow: "3px 3px 0 0 #FFC145" }}
          data-testid="discover-search-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="md:col-span-2 block">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                   style={{ color: "#475569", fontWeight: 700 }}>· Role, title, or keyword</div>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
                <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="e.g. product analyst"
                       className="w-full rounded px-3 py-2 text-[13px]"
                       style={{ paddingLeft: "2rem", border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                       data-testid="discover-q" />
              </div>
            </label>
            <label className="block">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                   style={{ color: "#475569", fontWeight: 700 }}>· Location</div>
              <div className="relative">
                <MapPin size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
                <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
                       placeholder="City or state"
                       className="w-full rounded px-3 py-2 text-[13px]"
                       style={{ paddingLeft: "2rem", border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                       data-testid="discover-location" />
              </div>
            </label>
            <label className="block">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                   style={{ color: "#475569", fontWeight: 700 }}>· Min. years exp.</div>
              <div className="relative">
                <Users size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
                <input type="number" min={0} max={40} value={minYears}
                       onChange={(e) => setMinYears(e.target.value)}
                       className="w-full rounded px-3 py-2 text-[13px]"
                       style={{ paddingLeft: "2rem", border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                       data-testid="discover-min-years" />
              </div>
            </label>
          </div>
          <label className="block mt-3">
            <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                 style={{ color: "#475569", fontWeight: 700 }}>· Required skills (comma-separated)</div>
            <input type="text" value={skills} onChange={(e) => setSkills(e.target.value)}
                   placeholder="SQL, Python, dashboards"
                   className="w-full rounded px-3 py-2 text-[13px]"
                   style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                   data-testid="discover-skills" />
          </label>
          <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
            <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
              {count === null ? "· Loading…" : `· ${count} candidate${count === 1 ? "" : "s"} matched`}
            </div>
            <button type="submit" disabled={busy}
                    className="inline-flex items-center gap-2 rounded-md px-4 py-2 font-mono-rm uppercase text-[11px] tracking-widest"
                    style={{
                      background: "#FF6A00", color: "white",
                      border: "2px solid #0F1B2D",
                      boxShadow: "3px 3px 0 0 #0F1B2D",
                      fontWeight: 700, cursor: busy ? "wait" : "pointer",
                    }}
                    data-testid="discover-search-submit">
              <Search size={12} /> {busy ? "Searching…" : "Search"}
            </button>
          </div>
        </form>
      </div>

      {/* Results grid */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
           data-testid="discover-results-grid">
        {results.map((r) => (
          <Link key={r.public_id} to={`/r/${r.public_id}`}
                className="block rounded-md p-4 transition-transform hover:translate-y-[-2px]"
                style={{
                  background: r.featured ? "#FFFBEB" : "white",
                  border: r.featured ? "1.5px solid #F59E0B" : "1.5px solid #CBD5E1",
                  boxShadow: "3px 3px 0 0 rgba(255,193,69,0.6), 0 8px 24px rgba(0,0,0,0.3)",
                  textDecoration: "none", color: "inherit",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "5px 5px 0 0 #FFC145, 0 12px 36px rgba(0,0,0,0.45)"; e.currentTarget.style.borderColor = "#0F1B2D"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "3px 3px 0 0 rgba(255,193,69,0.6), 0 8px 24px rgba(0,0,0,0.3)"; e.currentTarget.style.borderColor = r.featured ? "#F59E0B" : "#CBD5E1"; }}
                data-testid={`discover-card-${r.public_id}`}>
            {r.featured && (
              <span className="inline-flex items-center gap-1 font-mono-rm text-[9px] uppercase tracking-widest px-2 py-0.5 rounded mb-2"
                    style={{ background: "#F59E0B", color: "#0F1B2D", fontWeight: 700, border: "1px solid #0F1B2D" }}
                    data-testid="candidate-featured-badge">
                ★ Featured
              </span>
            )}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <div className="font-display uppercase leading-tight"
                     style={{ fontSize: "1.05rem", color: "#0F1B2D", letterSpacing: "-0.01em" }}>
                  {r.name_display}
                </div>
                <div className="text-[12px]" style={{ color: "#334155" }}>{r.headline}</div>
              </div>
              <span className="font-mono-rm text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                    style={{ background: "#FEF3C7", color: "#CA8A04", fontWeight: 700 }}>
                {r.years_experience || 0} yrs
              </span>
            </div>
            {r.location && (
              <div className="flex items-center gap-1 text-[11px] mb-2" style={{ color: "#64748B" }}>
                <MapPin size={11} /> {r.location}
              </div>
            )}
            <p className="text-[12px] leading-snug mb-3" style={{ color: "#334155" }}>
              {(r.summary || "").slice(0, 160)}{(r.summary || "").length > 160 && "…"}
            </p>
            {r.skills?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {r.skills.slice(0, 5).map((s, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded"
                        style={{ background: "#F1F5F9", color: "#334155", border: "1px solid #E2E8F0" }}>
                    {s}
                  </span>
                ))}
              </div>
            )}
            <div className="inline-flex items-center gap-1 text-[11px] font-mono-rm uppercase tracking-widest"
                 style={{ color: "#FF6A00", fontWeight: 700 }}>
              View résumé <ArrowRight size={11} />
            </div>
          </Link>
        ))}
        {results.length === 0 && !busy && (
          <div className="col-span-full text-center py-12 rounded-md"
               style={{ background: "white", border: "1px dashed #CBD5E1" }}
               data-testid="discover-empty-state">
            <Sparkles size={22} style={{ color: "#94A3B8" }} className="mx-auto mb-2" />
            <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1" style={{ color: "#94A3B8" }}>
              · No matches
            </div>
            <div className="text-[13px]" style={{ color: "#475569" }}>
              Try broadening your search or removing filters.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
